import { useUser } from '../../../../../hooks/useUser';
import { DeliveryCampaignType } from '../../../types';
import dayjs from '../../../../../utils/dayjs';
import { useMemo } from 'react';
import { FreeDeliveryCampaignWebshopViewModel } from '@kotipizzagroup/kotipizza-campaigns-api-client';
import { DeliveringRestaurantDto } from '@kotipizzagroup/kotipizza-shops-api-client';
import { useActiveFreeDeliveryCampaigns } from '../../../../../clients/campaigns-api';

export const getActiveCampaignType = (
  isPreOrder: boolean,
  freeDeliveryCampaign?: FreeDeliveryCampaignWebshopViewModel
): DeliveryCampaignType | null => {
  if (isPreOrder || !freeDeliveryCampaign) return null;

  const startDate = dayjs(freeDeliveryCampaign.start).tz('Europe/Helsinki');
  const endDate = dayjs(freeDeliveryCampaign.end).tz('Europe/Helsinki');
  const time = dayjs();

  if (!time.isBetween(startDate, endDate)) return null;
  if (
    freeDeliveryCampaign.days &&
    freeDeliveryCampaign.days.length > 0 &&
    !freeDeliveryCampaign.days.includes(time.weekday())
  ) {
    return null;
  }

  return freeDeliveryCampaign.assignedShops && freeDeliveryCampaign.assignedShops.length > 0
    ? DeliveryCampaignType.LOCAL_FREE_DELIVERY_CAMPAIGN
    : DeliveryCampaignType.FREE_DELIVERY_CAMPAIGN;
};

type UseDeliveryCampaignProps = {
  restaurant: DeliveringRestaurantDto;
  isPreorder: boolean;
};

export const useDeliveryCampaign = ({
  restaurant,
  isPreorder,
}: UseDeliveryCampaignProps): {
  campaignDeliveryFee: number | null;
  useCampaignPrice: boolean | undefined;
} => {
  const { data: freeDeliveryCampaigns = [] } = useActiveFreeDeliveryCampaigns();

  const freeDeliveryCampaign = useMemo(() => {
    if (freeDeliveryCampaigns.length < 1) {
      return undefined;
    }

    const globalFreeDeliveryCampaign = freeDeliveryCampaigns.find(
      (campaign) => !campaign.assignedShops || campaign.assignedShops.length < 1
    );
    if (globalFreeDeliveryCampaign) {
      return globalFreeDeliveryCampaign;
    }

    const { restaurantId } = restaurant;
    return freeDeliveryCampaigns.find((campaign) =>
      (campaign.assignedShops || []).includes(restaurantId || -1)
    );
  }, [restaurant, freeDeliveryCampaigns]);

  const deliveryCampaignType = useMemo(
    () => getActiveCampaignType(isPreorder, freeDeliveryCampaign),
    [freeDeliveryCampaign, isPreorder]
  );

  const { user } = useUser();

  const preorderBlocked = Boolean(
    freeDeliveryCampaign?.disablePreordersOnCampaign && isPreorder
  );

  let campaignDeliveryFee: number | null = null;

  if (
    deliveryCampaignType &&
    [
      DeliveryCampaignType.FREE_DELIVERY_CAMPAIGN,
      DeliveryCampaignType.LOCAL_FREE_DELIVERY_CAMPAIGN,
    ].includes(deliveryCampaignType)
  ) {
    campaignDeliveryFee = 0.0;
  }

  const useCampaignPrice =
    (!freeDeliveryCampaign?.requireLogIn || user?.subscribedToLoyalty) &&
    typeof campaignDeliveryFee === 'number' &&
    !preorderBlocked;

  return { campaignDeliveryFee, useCampaignPrice };
};
